import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 地理探索 - 横屏「地图标注」风格
 *
 * 核心亮点：
 * - 简化地图轮廓 SVG 描边动画
 * - 标注点脉冲弹入
 * - 地形剖面图（SVG 横截面）
 * - 气候/人口数据条形图
 * - 扫描雷达效果
 *
 * 尺寸：1920x1080 横屏
 */

interface MapPoint {
  name: string;
  x: number;
  y: number;
  info: string;
  icon: string;
  color: string;
}

interface GeoDataBar {
  label: string;
  value: number;
  max: number;
  color: string;
}

interface GeographyMapTemplateProps {
  topic: string;
  subtitle: string;
  mapOutlinePath: string;
  points: MapPoint[];
  dataBars: GeoDataBar[];
  funFact: string;
  keyNumber: { value: string; label: string };
  terrainProfile: number[];
}

const DEFAULT_PROPS: GeographyMapTemplateProps = {
  topic: '中国地理：地势三级阶梯',
  subtitle: 'China\'s Three-Step Terrain',
  mapOutlinePath: 'M400,120 Q500,100 560,140 Q620,160 660,200 Q680,260 700,300 Q720,350 700,400 Q680,440 640,460 Q600,480 560,500 Q520,520 480,510 Q440,500 400,480 Q360,460 340,420 Q320,380 300,340 Q280,300 280,260 Q290,220 320,180 Q350,140 400,120 Z',
  points: [
    { name: '青藏高原', x: 320, y: 260, info: '平均海拔 4000m+', icon: '🏔️', color: '#8b5cf6' },
    { name: '黄土高原', x: 440, y: 240, info: '海拔 1000-2000m', icon: '🏜️', color: '#f59e0b' },
    { name: '华北平原', x: 560, y: 280, info: '海拔 50m 以下', icon: '🌾', color: '#22c55e' },
    { name: '长江三角洲', x: 600, y: 360, info: '经济最发达区域', icon: '🏙️', color: '#3b82f6' },
  ],
  dataBars: [
    { label: '面积 (万km²)', value: 960, max: 1000, color: '#ef4444' },
    { label: '人口 (亿)', value: 14.1, max: 15, color: '#f59e0b' },
    { label: '最高海拔 (m)', value: 8849, max: 9000, color: '#8b5cf6' },
    { label: '海岸线 (万km)', value: 1.8, max: 3, color: '#3b82f6' },
  ],
  funFact: '中国从西到东横跨 5 个时区，但全国统一使用北京时间（UTC+8）',
  keyNumber: { value: '960 万', label: '国土面积 km²' },
  terrainProfile: [4500, 4200, 3800, 2500, 1800, 1200, 800, 400, 200, 100, 50, 30, 10],
};

export const GeographyMapTemplate: React.FC<Partial<GeographyMapTemplateProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerProgress = spring({ frame, fps, config: { damping: 18, stiffness: 90 } });
  const mapDraw = interpolate(frame, [30, 100], [0, 1], { extrapolateRight: 'clamp' });

  const pointStart = 80;
  const pointInterval = 40;

  const dataStart = pointStart + p.points.length * pointInterval + 20;
  const terrainStart = dataStart + 80;
  const terrainDraw = interpolate(frame, [terrainStart, terrainStart + 60], [0, 1], { extrapolateRight: 'clamp' });

  const factStart = terrainStart + 80;
  const factOpacity = interpolate(frame, [factStart, factStart + 25], [0, 1], { extrapolateRight: 'clamp' });

  // Radar sweep
  const radarAngle = (frame * 3) % 360;

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(135deg, #0c1222 0%, #0f1b33 50%, #0a1628 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Inter", sans-serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* Grid bg */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'linear-gradient(rgba(59,130,246,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,0.03) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      {/* Header */}
      <div
        style={{
          position: 'absolute',
          top: 32,
          left: 48,
          opacity: headerProgress,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <div style={{ padding: '4px 14px', background: '#3b82f618', border: '1px solid #3b82f640', borderRadius: 8, fontSize: 24, color: '#60a5fa', fontWeight: 600 }}>
            🌍 GEOGRAPHY
          </div>
        </div>
        <h1 style={{ fontSize: 36, fontWeight: 800, margin: 0 }}>{p.topic}</h1>
        <p style={{ fontSize: 28, color: 'rgba(255,255,255,0.3)', marginTop: 2 }}>{p.subtitle}</p>
      </div>

      {/* Key number */}
      <div
        style={{
          position: 'absolute',
          top: 50,
          right: 48,
          textAlign: 'right',
          opacity: headerProgress,
        }}
      >
        <div style={{ fontSize: 40, fontWeight: 900, color: '#60a5fa' }}>{p.keyNumber.value}</div>
        <div style={{ fontSize: 24, color: 'rgba(255,255,255,0.3)' }}>{p.keyNumber.label}</div>
      </div>

      {/* ========== MAP AREA ========== */}
      <svg
        style={{ position: 'absolute', left: 0, top: 240 }}
        width="800"
        height="560"
        viewBox="200 80 600 500"
      >
        {/* Radar sweep */}
        <defs>
          <linearGradient id="radarGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="0" />
            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.15" />
          </linearGradient>
        </defs>
        <g transform={`rotate(${radarAngle}, 500, 320)`}>
          <line x1="500" y1="320" x2="700" y2="320" stroke="url(#radarGrad)" strokeWidth="60" opacity={mapDraw} />
        </g>

        {/* Map outline */}
        <path
          d={p.mapOutlinePath}
          fill="rgba(59,130,246,0.04)"
          stroke="#3b82f6"
          strokeWidth="2"
          strokeDasharray="2000"
          strokeDashoffset={2000 * (1 - mapDraw)}
          opacity="0.6"
        />

        {/* Points */}
        {p.points.map((pt, i) => {
          const start = pointStart + i * pointInterval;
          const ptScale = spring({
            frame: frame - start,
            fps,
            from: 0,
            to: 1,
            config: { damping: 10, stiffness: 150, mass: 0.5 },
          });
          if (ptScale < 0.01) return null;

          const pulse = interpolate(Math.sin((frame - start) / 10 + i), [-1, 1], [8, 16]);

          return (
            <g key={i} opacity={ptScale}>
              <circle cx={pt.x} cy={pt.y} r={pulse} fill={pt.color} opacity="0.12" />
              <circle cx={pt.x} cy={pt.y} r="6" fill={pt.color} />
              <circle cx={pt.x} cy={pt.y} r="3" fill="white" />
            </g>
          );
        })}
      </svg>

      {/* Point labels (HTML) */}
      {p.points.map((pt, i) => {
        const start = pointStart + i * pointInterval;
        const opacity = interpolate(frame, [start + 10, start + 25], [0, 1], { extrapolateRight: 'clamp' });
        if (opacity < 0.05) return null;

        const htmlX = (pt.x - 200) * (800 / 600);
        const htmlY = (pt.y - 80) * (600 / 500) + 80;

        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: htmlX + 20,
              top: htmlY - 10,
              opacity,
              whiteSpace: 'nowrap',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 22 }}>{pt.icon}</span>
              <span style={{ fontSize: 22, fontWeight: 700, color: pt.color }}>{pt.name}</span>
            </div>
            <div style={{ fontSize: 20, color: 'rgba(255,255,255,0.4)', marginLeft: 26 }}>{pt.info}</div>
          </div>
        );
      })}

      {/* ========== RIGHT PANEL: Data bars ========== */}
      <div
        style={{
          position: 'absolute',
          top: 260,
          right: 350,
          width: 500,
        }}
      >
        <div style={{ fontSize: 28, color: 'rgba(255,255,255,0.3)', marginBottom: 14, letterSpacing: '0.1em' }}>基础数据</div>
        {p.dataBars.map((bar, i) => {
          const start = dataStart + i * 20;
          const barProgress = interpolate(frame, [start, start + 30], [0, bar.value / bar.max], { extrapolateRight: 'clamp' });
          const opacity = interpolate(frame, [start, start + 15], [0, 1], { extrapolateRight: 'clamp' });

          return (
            <div key={i} style={{ marginBottom: 18, opacity }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 24, color: 'rgba(255,255,255,0.5)' }}>{bar.label}</span>
                <span style={{ fontSize: 24, fontWeight: 700, color: bar.color }}>{bar.value}</span>
              </div>
              <div style={{ height: 12, background: 'rgba(255,255,255,0.04)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ width: `${barProgress * 100}%`, height: '100%', background: bar.color, borderRadius: 4 }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* ========== TERRAIN PROFILE ========== */}
      <div
        style={{
          position: 'absolute',
          bottom: 130,
          left: 48,
          right: 48,
        }}
      >
        <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.3)', marginBottom: 10 }}>地势剖面图 (西→东)</div>
        <svg width="1824" height="140" viewBox="0 0 1824 140">
          {/* Steps labels */}
          <text x="200" y="20" fill="#8b5cf680" fontSize="24" textAnchor="middle">第一阶梯</text>
          <text x="800" y="50" fill="#f59e0b80" fontSize="24" textAnchor="middle">第二阶梯</text>
          <text x="1400" y="80" fill="#22c55e80" fontSize="24" textAnchor="middle">第三阶梯</text>

          {/* Terrain filled area */}
          {(() => {
            const maxH = Math.max(...p.terrainProfile);
            const points = p.terrainProfile.map((h, i) => {
              const x = (i / (p.terrainProfile.length - 1)) * 1824;
              const y = 130 - (h / maxH) * 110;
              return `${x},${y}`;
            });
            const drawn = Math.floor(p.terrainProfile.length * terrainDraw);
            const drawnPoints = points.slice(0, drawn + 1);
            if (drawnPoints.length < 2) return null;

            const lastX = (drawn / (p.terrainProfile.length - 1)) * 1824;
            const areaD = `M0,130 L${drawnPoints.join(' L')} L${lastX},130 Z`;
            const lineD = `M${drawnPoints.join(' L')}`;

            return (
              <>
                <defs>
                  <linearGradient id="terrainGrad" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.15" />
                    <stop offset="50%" stopColor="#f59e0b" stopOpacity="0.1" />
                    <stop offset="100%" stopColor="#22c55e" stopOpacity="0.05" />
                  </linearGradient>
                </defs>
                <path d={areaD} fill="url(#terrainGrad)" />
                <path d={lineD} fill="none" stroke="#60a5fa" strokeWidth="2" opacity="0.6" />
              </>
            );
          })()}
        </svg>
      </div>

      {/* Fun fact */}
      <div
        style={{
          position: 'absolute',
          bottom: 40,
          left: 48,
          right: 48,
          opacity: factOpacity,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 18 }}>💡</span>
          <span style={{ fontSize: 22, color: 'rgba(255,255,255,0.6)' }}>{p.funFact}</span>
        </div>
      </div>

      {/* Progress */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 480], [0, 100], { extrapolateRight: 'clamp' })}%`,
          height: 3,
          background: 'linear-gradient(90deg, #8b5cf6, #f59e0b, #22c55e)',
        }}
      />
    </AbsoluteFill>
  );
};
